<template>
  <section id="reviews" class="services">
    <div class="row justify-between">
      <h1>{{ $t('DStudioReviewsTitle') }}</h1>
      <UiButtonArrowContainer v-if="!isMobile">
        <UiButtonArrow
          :right="false"
          @click="slideLeft"
        />
        <UiButtonArrow
          :right="true"
          @click="slideRight"
        />
      </UiButtonArrowContainer>
    </div>
    <div class="body">
      <div
        class="carousel"
        @mousemove="dragging"
        @mousedown="dragStart"
        @mouseup="dragStop"
        @mouseleave="dragStop"
        @touchstart="dragStart"
        @touchmove="dragging"
        @touchend="dragStop"
        ref="videoSliderRef"
      >
        <CardReview
          v-for="(review, index) in reviews"
          :id="review.id"
          :item="review"
          :ref="`videoItemRef${index}`"
          :class="{
            'gallery-video-loop_first': index === 0,
            'gallery-video-loop': true,
          }"
        />
      </div>
    </div>
    <div class="flex flex-center items-center">
      <UiButtonArrowContainer v-if="isMobile">
        <UiButtonArrow
          :right="false"
          @click="slideLeft"
        />
        <UiButtonArrow
          :right="true"
          @click="slideRight"
        />
      </UiButtonArrowContainer>
    </div>
  </section>
</template>

<script setup lang="ts">
import {useBaseStore} from '~/modules/BaseModule/base.store';
import {useDesignStudioStore} from '~/store/design-studio.store'

const runtimeConfig = useRuntimeConfig();
const staticUrl = runtimeConfig.public.vueAppStaticUrl;

const base = useBaseStore();
const designStudioStore = useDesignStudioStore();
const isMobile = computed(() => base.isMobile);
const videoSliderRef = ref(null);
const videoItemRef0 = ref(null);
const reviews = designStudioStore.getReviews(staticUrl);
const currentWidth = computed(() => base.currentWidth);

let positionDiff: number;
let firstImgWidth: number;
let isDragStart = false;
let prevPageX: number;
let prevScrollLeft: number;
let scrollWidth: number;
let isDragging = false;

const dragging = (e) => {
  if (videoSliderRef.value) {
    if (!isDragStart) return;
    // if (currentWidth.value > 769) {
    //   e.preventDefault();
    // }
    isDragging = true;
    videoSliderRef.value.classList.add('dragging');
    positionDiff = (e.pageX || e.touches[0].pageX) - prevPageX;
    videoSliderRef.value.scrollLeft = prevScrollLeft - positionDiff;
  }
};
const autoSlide = () => {
};

const calculateOffset = () => {
  const child = videoSliderRef.value.children[1] || videoSliderRef.value.children[0];
  const computedStyle = window.getComputedStyle(child);
  return child.offsetWidth + parseFloat(computedStyle.marginLeft) + 60;
}

const slideRight = () => {
  const offset = calculateOffset();
  videoSliderRef.value.scrollLeft += offset;
}

const slideLeft = () => {
  const offset = calculateOffset();
  videoSliderRef.value.scrollLeft -= offset;
}

const dragStart = (e) => {
  if (videoSliderRef.value) {
    isDragStart = true;
    prevPageX = e.pageX || e.touches[0].pageX;
    prevScrollLeft = videoSliderRef.value.scrollLeft;
  }
};

const dragStop = (e) => {
  isDragStart = false;
  if (videoSliderRef.value) {
    videoSliderRef.value.classList.remove('dragging');
    if (!isDragging) return;
    setTimeout(() => {
      isDragging = false;
    }, 0);
  }
};

watch(
  () => [isMobile.value],
  () => {
    if (videoSliderRef.value) {
      videoSliderRef.value.scrollLeft = 0;
      setTimeout(() => {
        const additionalValueML = isMobile.value ? 20 : 40;
        firstImgWidth =
          videoSliderRef.value.clientWidth + additionalValueML;
        scrollWidth =
          videoSliderRef.value.scrollWidth -
          videoSliderRef.value.clientWidth;
      }, 100);
    }
  },
);

const resizeHandler = () => {
  if (videoItemRef0.value) {
    const additionalValueML = isMobile.value ? 20 : 40;
    firstImgWidth =
      videoSliderRef.value.clientWidth + additionalValueML;
    const afterSlide = autoSlide();
    if (afterSlide % firstImgWidth !== 0) {
      videoSliderRef.value.scrollLeft =
        firstImgWidth *
        Math.round(videoSliderRef.value.scrollLeft / firstImgWidth);
    }
  }
};

onMounted(() => {
  if (videoSliderRef.value) {
    const additionalValueML = isMobile.value ? 20 : 40;
    firstImgWidth =
      videoSliderRef.value.clientWidth + additionalValueML;
    scrollWidth =
      videoSliderRef.value.scrollWidth -
      videoSliderRef.value.clientWidth;
  }

  window.onresize = resizeHandler;
});
onUnmounted(() => {
  window.onresize = null;
});
</script>

<style scoped lang="scss">
.services {
  width: 100%;
  display: flex;
  flex-direction: column;
  height: auto;
  margin-top: $m-45;

  .body {
    margin-top: $m-12;

    @media (max-width: $md) {
      margin-top: $m-2;
    }
  }

  @media (min-width: 2000px) {
    min-height: unset;
  }
  @media (max-width: $lg) {
    width: 100%;
  }
  @media (max-width: $md) {
    gap: $g-5;
    margin-top: $m-40;
  }

  h1 {
    @include title-fw-700-fs-56-black-100;
    margin: 0px;
  }
}

.carousel {
  width: calc(100% + 40px);
  overflow: hidden;
  scroll-behavior: smooth;
  display: flex;
  cursor: pointer;
  margin-left: -20px;
  padding: 0px 40px 20px;

  .card-review {
    width: 100%;
    max-width: 390px;
    @media (max-width: $lg) {
      width: calc(100% / 1);
    }
    @media (max-width: $md) {
      width: calc(100% / 1);
    }
  }

  .gallery-video-loop {
    margin-left: 40px;

    &_first {
      margin-left: 0px;
    }

    @media (max-width: $md) {
      margin-left: 20px;
      &_first {
        margin-left: 0px;
      }
    }
  }
}

.dragging {
  scroll-behavior: auto;

  .card-review {
    pointer-events: none;
    cursor: grab !important;
  }
}

.dragging {
  .card-review {
    cursor: grabbing !important;
  }
}

img {
  pointer-events: none;
  cursor: grab !important;
}
</style>
